/**
 * @file src/shell/registry.js
 * ĐÂY LÀ FILE GIẢI QUYẾT NỢ KIẾN TRÚC LỚN NHẤT của three-lessons.
 *
 * Vấn đề ở bản cũ: popup "Hải lưu" (3D/2D) được viết cứng trong index.html và
 * ~110/200 dòng của UIManager.js là logic riêng của tiết 1. Nhân lên 1000 bài thì
 * tiết 2 cần popup khác → phải sửa code dùng chung → chạm vào cả 1000 bài.
 *
 * Cách giải: shell chỉ cấp REGISTRY. Tiết tự đăng ký nút/tab/panel/popup của mình.
 * LUẬT: không file nào trong src/shell/ được chứa tên của bất kỳ tiết nào.
 */

/**
 * @template T
 * @typedef {Object} Slot
 * @property {string} id
 * @property {number} order
 * @property {T} data
 */

/**
 * @template T
 * @typedef {Object} Registry
 * @property {(id: string, data: T, order?: number) => { update: (d: Partial<T>) => void, remove: () => void }} add
 * @property {() => Array<Slot<T>>} list    Đã sort theo order
 * @property {(id: string) => Slot<T> | undefined} get
 * @property {(cb: () => void) => () => void} onChange
 * @property {() => void} clear
 */

/**
 * @template T
 * @param {{ name: string, reservedIds?: string[] }} opts
 * @returns {Registry<T>}
 */
export function createRegistry(opts) {
  /** @type {Map<string, Slot<T>>} */
  const slots = new Map();
  /** @type {Set<() => void>} */
  const listeners = new Set();
  const reserved = new Set(opts.reservedIds ?? []);

  const notify = () => {
    for (const l of listeners) l();
  };

  return {
    add(id, data, order = 10) {
      let finalId = id;

      // BUG ĐÃ SỬA: trước đây `reserved.has(id)` một mình quyết định suffix, nên chặn
      // luôn CẢ lần add() ĐẦU TIÊN — đúng lần SHELL tự đăng ký 5 id của mình (Shell.js
      // gọi buttons.add('rotate', …) v.v., xem ToolRail.js) cũng tự bị đổi thành
      // "rotate-2". "Không bao giờ để tiết ghi đè nút của shell" chỉ có nghĩa khi ĐÃ CÓ
      // ai chiếm `id` này rồi (slots.has(id)) — thêm điều kiện đó thì lần đăng ký GỐC
      // của shell không còn tự đụng vào chính nó nữa, chỉ lần đăng ký THỨ HAI (của tiết,
      // trùng id) mới bị đổi.
      if (reserved.has(id) && slots.has(id)) {
        finalId = `${id}-2`;
        console.warn(
          `[${opts.name}] id "${id}" là của shell. Đã đổi thành "${finalId}". Chọn id khác.`,
        );
      }
      if (slots.has(finalId)) {
        console.warn(`[${opts.name}] id "${finalId}" đã tồn tại — ghi đè.`);
      }

      slots.set(finalId, { id: finalId, order, data });
      notify();

      return {
        update(patch) {
          const s = slots.get(finalId);
          if (!s) return;
          s.data = { ...s.data, ...patch };
          notify();
        },
        remove() {
          slots.delete(finalId);
          notify();
        },
      };
    },

    list() {
      return [...slots.values()].sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
    },

    get(id) {
      return slots.get(id);
    },

    onChange(cb) {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },

    clear() {
      slots.clear();
      notify();
    },
  };
}
